import MapMarker from '@assets/icons/map-marker.svg';
import { useMap } from '@hooks/useMap';
import { GoogleMap, GoogleMapProps, MarkerF } from '@react-google-maps/api';
import { ISoftwareIntegratorDto } from '@softwareIntegrator/types/softwareIntegrator';
import { groupByCityName } from '@softwareIntegrator/utils/softwareIntegrators';
import { useCallback, useMemo, useState } from 'react';
import './index.css';
import { MapPopup } from './MapPopup';

interface IProps extends GoogleMapProps {
    integrators: ISoftwareIntegratorDto[];
    onMarkerButtonClick: (integrator: ISoftwareIntegratorDto) => void;
}

const MAP_CENTER = { lat: 0, lng: 30 };

const mapStyles = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d4dadc' }] },
];

const restriction = {
    latLngBounds: {
        north: 85,
        south: -85,
        west: -180,
        east: 180,
    },
    strictBounds: true,
};

export const Map = ({ integrators, onMarkerButtonClick, zoom, center, ...otherProps }: IProps) => {
    const [selectedMarker, setSelectedMarker] = useState<null | ISoftwareIntegratorDto[]>(null);
    const { isLoaded } = useMap();

    const groupedIntegrators = useMemo(() => groupByCityName(integrators), [integrators]);

    const handleMarkerClickFactory = useCallback((integrators: ISoftwareIntegratorDto[]) => {
        return () => {
            if (integrators) setSelectedMarker(integrators);
        };
    }, []);

    const handleClose = useCallback(() => setSelectedMarker(null), []);

    return (
        isLoaded && (
            <GoogleMap
                mapContainerStyle={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 8,
                }}
                center={center ?? MAP_CENTER}
                zoom={zoom ?? 0.001}
                options={{
                    styles: mapStyles,
                    restriction,
                    mapTypeControl: false, // Hides the Satellite/Roadmap toggle
                    streetViewControl: false,
                    fullscreenControl: false,
                }}
                {...otherProps}
            >
                {Object.entries(groupedIntegrators).map(([country, integrators]) => (
                    <MarkerF
                        key={country}
                        position={{ lat: integrators[0].latitude, lng: integrators[0].longitude }}
                        onClick={handleMarkerClickFactory(integrators)}
                        icon={MapMarker}
                    />
                ))}
                {selectedMarker && (
                    <MapPopup
                        integrators={selectedMarker}
                        onCloseClick={handleClose}
                        onButtonClick={onMarkerButtonClick}
                        // Pass the group country and city as a key to force a re-render
                        key={`${selectedMarker[0].country.name}-${selectedMarker[0].city}`}
                    />
                )}
            </GoogleMap>
        )
    );
};
